// WHERE EACH PART OF THE FREE CHECK GOES IN ITS POP-UP, AS PURE VALUES (R-pending-web-free-first).
//
// THE APP'S LAYOUT, PORTED: `FreeCheckExtrasLayout.sections` (AppealAce/Features/Checker/FreeCheckExtrasLayout.swift) and
// `FreeCheckRendering` (FreeCheckRendering.swift), which are themselves the marketing site's `free-check-extras.js` `plan`.
// The server's screen and extras in, the sections out, top to bottom: the deadline card, the screen's own lines, how much
// values rose, the tax cost, the tax breaks, the price trend, homes around you, the appeal rate, the estimator, the "not
// completed" line and the close. ONLY WHAT IS PRESENT IS A SECTION. Every rule here keys on a line's id — never on its
// text, never on a figure — and decides how a line LOOKS or where it SITS, never whether it is said: the server decided
// that. Registered at birth in docs/THIN_CLIENT_DEBT.md (Bucket A: the website's copy of the app's layout order, held to
// the server's recordings by test/freeFirst.test.tsx). Migration: the server names the sections.
//
// THREE OF THE SERVER'S LINES MOVE, AND ONLY WHEN THERE ARE EXTRAS, as in the app:
//   - `freecheck.cta.evaluate` becomes the amber button under "Add to calendar" (with no deadline card, it stays the last
//     row of the screen's lines, the button it has always been);
//   - `freecheck.analysis.notDone` sits over the close (only when there is a close);
//   - `freecheck.deadline.known` is left to the deadline card, which states the same date (only when there is one).

import type { FreeCheckExtras, FreeCheckLine, FreeCheckScreen } from "@contract";

/** The one id the client acts on: the server's "find out if you have a case" line is the button into the purchase. */
export const PROCEED_LINE_ID = "freecheck.cta.evaluate";
export const NOT_DONE_LINE_ID = "freecheck.analysis.notDone";
export const DEADLINE_LINE_ID = "freecheck.deadline.known";

/** A deadline this close (days) is drawn in amber, as the app's and the site's are (`urgentDays`). How it LOOKS only. */
export const URGENT_DAYS = 30;

/** How a line of the screen is drawn, by its id's prefix (`FreeCheckRendering.group`). */
export type LineGroup = "lead" | "record" | "notice" | "body";

export function lineGroup(id: string): LineGroup {
  if (id.startsWith("freecheck.unavailable.")) return "notice";
  if (id.startsWith("freecheck.record.")) return "record";
  if (id.startsWith("freecheck.found.")) return "lead";
  return "body";
}

export type Section =
  | { readonly kind: "deadline"; readonly deadline: NonNullable<FreeCheckExtras["deadline"]>; readonly proceed: FreeCheckLine | null }
  | { readonly kind: "lines"; readonly lines: readonly FreeCheckLine[] }
  | { readonly kind: "rollChange"; readonly card: NonNullable<FreeCheckExtras["rollChange"]> }
  | { readonly kind: "taxCost"; readonly card: NonNullable<FreeCheckExtras["taxCost"]> }
  | { readonly kind: "exemptions"; readonly items: NonNullable<FreeCheckExtras["exemptions"]> }
  | { readonly kind: "priceTrend"; readonly card: NonNullable<FreeCheckExtras["priceTrend"]> }
  | { readonly kind: "homeValues"; readonly card: NonNullable<FreeCheckExtras["homeValues"]> }
  | { readonly kind: "appealRate"; readonly card: NonNullable<FreeCheckExtras["appealRate"]> }
  | { readonly kind: "estimator"; readonly card: FreeCheckExtras["estimator"] }
  | { readonly kind: "notDone"; readonly line: FreeCheckLine }
  | { readonly kind: "close"; readonly card: FreeCheckExtras["close"] };

/** Whether the screen is about the property (it opens with a `freecheck.found.*` line) rather than a refusal. */
export function showsTheAddress(lines: readonly FreeCheckLine[]): boolean {
  return lines.length > 0 && lineGroup(lines[0].id) === "lead";
}

/** The sections, in order, and nothing for what is absent (`FreeCheckExtrasLayout.sections`). */
export function panelSections(screen: FreeCheckScreen): Section[] {
  const extras = screen.extras;
  if (extras === null || !showsTheAddress(screen.lines)) return [{ kind: "lines", lines: screen.lines }];
  const flow: FreeCheckLine[] = [];
  let proceed: FreeCheckLine | null = null;
  let notDone: FreeCheckLine | null = null;
  for (const line of screen.lines) {
    if (line.id === PROCEED_LINE_ID && proceed === null) { proceed = line; continue; }
    if (line.id === NOT_DONE_LINE_ID && notDone === null) { notDone = line; continue; }
    if (line.id === DEADLINE_LINE_ID && extras.deadline !== undefined) continue;
    flow.push(line);
  }
  const out: Section[] = [];
  if (extras.deadline !== undefined) out.push({ kind: "deadline", deadline: extras.deadline, proceed });
  else if (proceed !== null) flow.push(proceed);
  if (flow.length > 0) out.push({ kind: "lines", lines: flow });
  if (extras.rollChange !== undefined) out.push({ kind: "rollChange", card: extras.rollChange });
  if (extras.taxCost !== undefined) out.push({ kind: "taxCost", card: extras.taxCost });
  if (extras.exemptions !== undefined && extras.exemptions.length > 0) out.push({ kind: "exemptions", items: extras.exemptions });
  if (extras.priceTrend !== undefined) out.push({ kind: "priceTrend", card: extras.priceTrend });
  if (extras.homeValues !== undefined) out.push({ kind: "homeValues", card: extras.homeValues });
  if (extras.appealRate !== undefined) out.push({ kind: "appealRate", card: extras.appealRate });
  out.push({ kind: "estimator", card: extras.estimator });
  if (notDone !== null) out.push({ kind: "notDone", line: notDone });
  out.push({ kind: "close", card: extras.close });
  return out;
}

/** Consecutive lines of one group, so a run of record lines is drawn as one list. Flattened, they are the lines again. */
export function groupRuns(lines: readonly FreeCheckLine[]): FreeCheckLine[][] {
  const out: FreeCheckLine[][] = [];
  for (const line of lines) {
    const last = out[out.length - 1];
    if (last !== undefined && lineGroup(last[0].id) === lineGroup(line.id)) last.push(line);
    else out.push([line]);
  }
  return out;
}

/** An href a page can open, as the server sent it: https with a host, or null — then the line is text. */
export function linkUrl(href: string | undefined): string | null {
  if (href === undefined) return null;
  try {
    const url = new URL(href.trim());
    return url.protocol === "https:" && url.host !== "" ? url.toString() : null;
  } catch {
    return null;
  }
}

/** The close's lines as the card draws them: the title, the body, and (of three or more) the last as the unlock button. */
export function closeParts(close: FreeCheckExtras["close"]): { title: FreeCheckLine; body: FreeCheckLine[]; unlock: FreeCheckLine | null } | null {
  const [title, ...rest] = close.lines;
  if (title === undefined) return null;
  const unlock = close.lines.length >= 3 ? rest[rest.length - 1] : null;
  return { title, body: unlock === null ? rest : rest.slice(0, -1), unlock };
}

/** The "yours went up more" lines and the closer-look line, which are drawn as one block (`FreeCheckRendering`). */
const YOURS_MORE = new Set(["freecheck.rollChange.yoursMoreTypical", "freecheck.rollChange.yoursMoreCounty"]);
const CLOSER_LOOK = "freecheck.rollChange.closerLook";

/** A sourced card's lines in groups: a "yours went up more" line and the closer-look line after it are one group. */
export function sourcedGroups(lines: readonly FreeCheckLine[]): FreeCheckLine[][] {
  const out: FreeCheckLine[][] = [];
  for (const line of lines) {
    const last = out[out.length - 1];
    if (line.id === CLOSER_LOOK && last !== undefined && YOURS_MORE.has(last[last.length - 1].id)) last.push(line);
    else out.push([line]);
  }
  return out;
}

// ── Emphasis inside a server sentence: the TEXT never changes; only how runs of it are set ───────────────────────────

export type Emphasis = "strong" | "money";
export interface Run { readonly text: string; readonly emphasis: Emphasis | null }
type Rule = readonly [RegExp, Emphasis];

/** The tax card's per-$10,000 line, an exemption's line, and the appeal-rate line (the app's `…Rules`). */
export const PER_10K_RULES: readonly Rule[] = [[/\$10,000/, "strong"], [/\$[\d,]+ a year/, "money"]];
export const MONEY_RULES: readonly Rule[] = [[/\$[\d,]+ a year/, "money"]];
export const SHARE_RULES: readonly Rule[] = [[/\b\d+ in 10\b/, "strong"]];

/** The sentence in runs: the earliest match of any unused rule wins, each rule used once. Joined, the runs are the text. */
export function runs(text: string, rules: readonly Rule[]): Run[] {
  const out: Run[] = [];
  let rest = text;
  const used = new Set<number>();
  for (;;) {
    let at = -1;
    let length = 0;
    let rule = -1;
    for (let i = 0; i < rules.length; i++) {
      if (used.has(i)) continue;
      const m = rules[i][0].exec(rest);
      if (m === null || m[0].length === 0) continue;
      if (rule < 0 || m.index < at) {
        at = m.index;
        length = m[0].length;
        rule = i;
      }
    }
    if (rule < 0) break;
    used.add(rule);
    if (at > 0) out.push({ text: rest.slice(0, at), emphasis: null });
    out.push({ text: rest.slice(at, at + length), emphasis: rules[rule][1] });
    rest = rest.slice(at + length);
  }
  if (rest.length > 0) out.push({ text: rest, emphasis: null });
  return out;
}

/** The index as an SVG polyline's points in a `w` × `h` box, oldest left, highest top; "" for fewer than two values. */
export function sparkPoints(series: readonly number[], w: number, h: number, inset = 3): string {
  if (series.length < 2) return "";
  const lo = Math.min(...series);
  const hi = Math.max(...series);
  const span = hi - lo;
  const step = (w - 2 * inset) / (series.length - 1);
  return series
    .map((v, i) => {
      const y = span === 0 ? h / 2 : inset + ((hi - v) / span) * (h - 2 * inset);
      return `${(inset + i * step).toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

// ── "Add to calendar": an .ics file made on the device, the site's own (free-check-extras.js `ics`) ──────────────────

const pad = (n: number, w: number): string => String(n).padStart(w, "0");
const ymd = (t: number): string => {
  const d = new Date(t);
  return `${pad(d.getUTCFullYear(), 4)}${pad(d.getUTCMonth() + 1, 2)}${pad(d.getUTCDate(), 2)}`;
};
const icsText = (s: string): string => s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r\n|\r|\n/g, "\\n");

/** RFC 5545 folding: no line longer than 75 octets of UTF-8; a continuation starts with one space. */
function fold(line: string): string {
  const out: string[] = [];
  let cur = "";
  let bytes = 0;
  for (const ch of line) {
    const b = new TextEncoder().encode(ch).length;
    if (bytes + b > 75) {
      out.push(cur);
      cur = ` ${ch}`;
      bytes = 1 + b;
    } else {
      cur += ch;
      bytes += b;
    }
  }
  out.push(cur);
  return out.join("\r\n");
}

/** A short, stable tag for the event's UID (FNV-1a), so saving the same deadline twice updates one event. */
function tag(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** THE .ics FILE for the server's `{title, date}`: one all-day event on that day, its summary the server's title, shown
 *  as free time. Nothing else goes into it. Null when the date is not a real one. */
export function icsFile(calendar: { title: string; date: string }, now: Date): { name: string; text: string } | null {
  const t = /^\d{4}-\d{2}-\d{2}$/.test(calendar.date) ? Date.parse(`${calendar.date}T00:00:00Z`) : NaN;
  const title = calendar.title.trim();
  if (!Number.isFinite(t) || ymd(t) !== calendar.date.replace(/-/g, "") || title === "") return null;
  const stamp = Number.isFinite(now.getTime()) ? now : new Date(0);
  const lines = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//PROppeal//Free check//EN", "CALSCALE:GREGORIAN", "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:appeal-deadline-${ymd(t)}-${tag(title)}@proppeal.com`,
    `DTSTAMP:${ymd(stamp.getTime())}T${pad(stamp.getUTCHours(), 2)}${pad(stamp.getUTCMinutes(), 2)}${pad(stamp.getUTCSeconds(), 2)}Z`,
    `DTSTART;VALUE=DATE:${ymd(t)}`,
    `DTEND;VALUE=DATE:${ymd(t + 86_400_000)}`,
    `SUMMARY:${icsText(title)}`,
    "TRANSP:TRANSPARENT",
    "END:VEVENT", "END:VCALENDAR",
  ];
  return { name: `appeal-deadline-${calendar.date}.ics`, text: `${lines.map(fold).join("\r\n")}\r\n` };
}
